/**
 * Milestone node helpers — host attachment, reached state, layout constants.
 *
 * Milestones are passive markers attached to a host mission:
 *   side 'input'  → "when mission is ready"  (upstream fan-in through milestone)
 *   side 'output' → "when mission is complete" (downstream fan-out through milestone)
 */

import { getEffectiveDependencyIds } from '../layout/graphBuilder';
import { evaluateUnlockConditions } from './dependencyUtils/evaluateConditions';
import { calculateUpstreamWorkTimeRange, isTaskCompleteForProgress } from './progressUtils';
import { formatDurationRange } from '../../../../utils/taskDuration';
import { DEFAULT_STATUSES } from '../TaskNode/constants';
import { isNoteNode } from './noteUtils';

/** Visual junction ring diameter — also the edge-connection box (lines converge here). */
export const MILESTONE_JUNCTION_SIZE = 14;
export const MILESTONE_LABEL_GAP = 12;
/** Label body width — matches a normal mission card. */
export const MILESTONE_LABEL_WIDTH = 290;

/**
 * Layout / ELK / edge-routing FOOTPRINT (opaque obstacle).
 * Asymmetric: the junction ring sits at ONE edge (on the dependency line) and the
 * mission-card-width label fills the rest. The whole rectangle is treated as opaque
 * so other dependency lines route around it.
 */
export const MILESTONE_NODE_WIDTH = MILESTONE_JUNCTION_SIZE + MILESTONE_LABEL_GAP + MILESTONE_LABEL_WIDTH;
export const MILESTONE_NODE_HEIGHT = 104;

const DEFAULT_HOST_WIDTH = 290;
const DEFAULT_HOST_HEIGHT = 150;
const MILESTONE_STACK_GAP = MILESTONE_NODE_HEIGHT + 24;
/** Gap between the host edge and the junction (line distance, not box distance). */
const MILESTONE_HOST_GAP = 48;

export function getMilestoneLayoutDims(nodeDimensions, taskId) {
  const measured = nodeDimensions?.[taskId];
  if (measured?.width && measured?.height) {
    return { width: measured.width, height: measured.height };
  }
  return { width: MILESTONE_NODE_WIDTH, height: MILESTONE_NODE_HEIGHT };
}

/**
 * Which side of the junction the label sits on. Stable (doesn't depend on the
 * junction position itself): compares the host column to the footprint centre.
 * Defaults to 'right'; flips to 'left' when the host sits to the right.
 */
export function getMilestoneLabelSide(footprintPos, footprintDims, hostPos, hostDims) {
  if (!hostPos) return 'right';
  const w = footprintDims?.width ?? MILESTONE_NODE_WIDTH;
  const fpCenterX = footprintPos.x + w / 2;
  const hostCenterX = hostPos.x + (hostDims?.width ?? DEFAULT_HOST_WIDTH) / 2;
  return hostCenterX > fpCenterX + 24 ? 'left' : 'right';
}

/** Junction ring centre point for a footprint, given which side the label is on. */
export function getMilestoneJunctionCenter(footprintPos, footprintDims, side = 'right') {
  const w = footprintDims?.width ?? MILESTONE_NODE_WIDTH;
  const h = footprintDims?.height ?? MILESTONE_NODE_HEIGHT;
  const cy = footprintPos.y + h / 2;
  const cx = side === 'left'
    ? footprintPos.x + w - MILESTONE_JUNCTION_SIZE / 2
    : footprintPos.x + MILESTONE_JUNCTION_SIZE / 2;
  return { x: cx, y: cy };
}

/** Canvas rect of the visible label body (used for the drag handle anchor). */
export function getMilestoneLabelRect(footprintPos, footprintDims, side = 'right') {
  const w = footprintDims?.width ?? MILESTONE_NODE_WIDTH;
  const h = footprintDims?.height ?? MILESTONE_NODE_HEIGHT;
  const x = side === 'left'
    ? footprintPos.x
    : footprintPos.x + MILESTONE_JUNCTION_SIZE + MILESTONE_LABEL_GAP;
  return { x, y: footprintPos.y, width: MILESTONE_LABEL_WIDTH, height: h };
}

/**
 * Edge connection geometry: a tiny box centred on the junction so all dependency
 * lines converge on the single junction point.
 */
export function getMilestoneConnectionGeometry(footprintPos, footprintDims, side = 'right') {
  const center = getMilestoneJunctionCenter(footprintPos, footprintDims, side);
  return {
    pos: { x: center.x - MILESTONE_JUNCTION_SIZE / 2, y: center.y - MILESTONE_JUNCTION_SIZE / 2 },
    dims: { width: MILESTONE_JUNCTION_SIZE, height: MILESTONE_JUNCTION_SIZE },
  };
}

/**
 * Canvas top-left for the milestone FOOTPRINT, positioned so the junction lands on the
 * host mission column, one gap above (input) or below (output) the host edge.
 */
export function getMilestoneJunctionPosition(hostPos, hostDims, side, stackIndex = 0, labelSide = 'right') {
  const hostWidth = hostDims?.width ?? DEFAULT_HOST_WIDTH;
  const hostHeight = hostDims?.height ?? DEFAULT_HOST_HEIGHT;
  const halfH = MILESTONE_NODE_HEIGHT / 2;
  const junctionCenterX = hostPos.x + hostWidth / 2;
  const x = labelSide === 'left'
    ? junctionCenterX - (MILESTONE_NODE_WIDTH - MILESTONE_JUNCTION_SIZE / 2)
    : junctionCenterX - MILESTONE_JUNCTION_SIZE / 2;

  if (side === 'input') {
    const junctionY = hostPos.y - MILESTONE_HOST_GAP - stackIndex * MILESTONE_STACK_GAP;
    return { x, y: junctionY - halfH };
  }
  const junctionY = hostPos.y + hostHeight + MILESTONE_HOST_GAP + stackIndex * MILESTONE_STACK_GAP;
  return { x, y: junctionY - halfH };
}

export function isMilestoneNode(task) {
  return task?.nodeType === 'milestone';
}

/** Tasks that participate in chain header braces (missions/decisions only). */
export function getNonMilestoneTasks(tasks) {
  return (tasks || []).filter((t) => !isMilestoneNode(t) && !isNoteNode(t));
}

export function getNonMilestoneTaskIds(tasks) {
  return getNonMilestoneTasks(tasks).map((t) => t.id);
}

/** @returns {{ hostTaskId: string, side: 'input'|'output' } | null} */
export function getMilestoneAnchor(milestone) {
  if (!milestone) return null;
  const anchor = milestone.milestoneAnchor;
  if (anchor?.hostTaskId && (anchor.side === 'input' || anchor.side === 'output')) {
    return anchor;
  }
  return null;
}

/** Milestones grouped by host id and side (sorted by task order). */
export function buildMilestonesByHostMap(tasks) {
  const inputByHost = new Map();
  const outputByHost = new Map();

  (tasks || [])
    .filter(isMilestoneNode)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((milestone) => {
      const anchor = getMilestoneAnchor(milestone);
      if (!anchor) return;
      const map = anchor.side === 'input' ? inputByHost : outputByHost;
      if (!map.has(anchor.hostTaskId)) map.set(anchor.hostTaskId, []);
      map.get(anchor.hostTaskId).push(milestone);
    });

  return { inputByHost, outputByHost };
}

export function getHostTask(milestone, tasks) {
  const anchor = getMilestoneAnchor(milestone);
  if (!anchor) return null;
  return tasks.find((t) => t.id === anchor.hostTaskId && !isMilestoneNode(t)) || null;
}

export function isMilestoneReached(milestone, tasks, nestedSprints = [], statuses = DEFAULT_STATUSES) {
  const host = getHostTask(milestone, tasks);
  if (!host) return false;

  const anchor = getMilestoneAnchor(milestone);
  const taskMap = new Map(tasks.map((t) => [t.id, t]));

  if (anchor?.side === 'output') {
    return isTaskCompleteForProgress(host, statuses);
  }

  const { isSatisfied } = evaluateUnlockConditions(
    host.unlockConditions,
    taskMap,
    nestedSprints,
    statuses,
  );
  return isSatisfied;
}

/** @typedef {{ totalMinutes: number, minMinutes: number, maxMinutes: number, missingEstCount: number, incompleteCount: number }} RemainingWorkResult */

function emptyRemainingWorkResult() {
  return {
    totalMinutes: 0,
    minMinutes: 0,
    maxMinutes: 0,
    missingEstCount: 0,
    incompleteCount: 0,
  };
}

/** If more than this share of incomplete upstream missions lack est. time, hide the countdown. */
export const MILESTONE_EST_MISSING_THRESHOLD = 0.2;

/**
 * Time until the milestone is reached.
 * Parallel required missions are summed. Open choices become a fastest~longest range.
 */
export function calculateTimeUntilMilestone(
  milestone,
  tasks,
  statuses = DEFAULT_STATUSES,
  nestedSprints = [],
  allSprints = [],
) {
  const host = getHostTask(milestone, tasks);
  if (!host) return emptyRemainingWorkResult();

  const anchor = getMilestoneAnchor(milestone);
  const missionTasks = tasks.filter((t) => !isMilestoneNode(t));

  if (anchor?.side === 'output') {
    if (isTaskCompleteForProgress(host, statuses)) {
      return emptyRemainingWorkResult();
    }
    return calculateUpstreamWorkTimeRange(missionTasks, [host.id], {
      statuses,
      nestedSprints,
      allSprints,
      requiredTaskIds: [host.id],
    });
  }

  const { isSatisfied } = evaluateUnlockConditions(
    host.unlockConditions,
    new Map(tasks.map((t) => [t.id, t])),
    nestedSprints,
    statuses,
  );
  if (isSatisfied) return emptyRemainingWorkResult();

  const deps = getEffectiveDependencyIds(host, missionTasks);
  if (deps.length === 0) {
    return emptyRemainingWorkResult();
  }

  return calculateUpstreamWorkTimeRange(missionTasks, deps, {
    statuses,
    nestedSprints,
    allSprints,
    requiredTaskIds: [host.id],
  });
}

/**
 * Meta line for milestone cards: Reached (with tick), time remaining, or Not Reached.
 * @returns {{ kind: 'reached' | 'time' | 'not_reached', text: string }}
 */
export function getMilestoneMetaLabel(
  milestone,
  tasks,
  statuses = DEFAULT_STATUSES,
  nestedSprints = [],
  allSprints = [],
) {
  if (isMilestoneReached(milestone, tasks, nestedSprints, statuses)) {
    return { kind: 'reached', text: 'Reached' };
  }

  const { minMinutes, maxMinutes, missingEstCount, incompleteCount } = calculateTimeUntilMilestone(
    milestone, tasks, statuses, nestedSprints, allSprints,
  );

  const missingShare = incompleteCount > 0 ? missingEstCount / incompleteCount : 0;
  if (incompleteCount === 0 || missingShare > MILESTONE_EST_MISSING_THRESHOLD || maxMinutes <= 0) {
    return { kind: 'not_reached', text: 'Not Reached' };
  }

  return { kind: 'time', text: `${formatDurationRange(minMinutes, maxMinutes)} remaining` };
}

/** Suggested canvas position when attaching milestone to a host. */
export function suggestMilestonePosition(hostTask, side, nodePositions = {}, existingMilestones = []) {
  const hostPos = nodePositions[hostTask.id] || { x: hostTask.x ?? 0, y: hostTask.y ?? 0 };
  return getMilestoneJunctionPosition(hostPos, null, side, existingMilestones.length);
}

/**
 * Place attached milestones relative to their host after sync layout (non-ELK).
 * Milestones are not in the dependency graph, so assignLayers skips them.
 */
export function applyMilestonePositionsAfterSyncLayout(positions, tasks, nodeDimensions = {}) {
  const { inputByHost, outputByHost } = buildMilestonesByHostMap(tasks);

  (tasks || []).forEach((host) => {
    if (isMilestoneNode(host)) return;
    const hostPos = positions[host.id];
    if (!hostPos) return;

    const hostDims = nodeDimensions[host.id] || { width: DEFAULT_HOST_WIDTH, height: DEFAULT_HOST_HEIGHT };

    (inputByHost.get(host.id) || []).forEach((ms, index) => {
      positions[ms.id] = getMilestoneJunctionPosition(hostPos, hostDims, 'input', index);
    });

    (outputByHost.get(host.id) || []).forEach((ms, index) => {
      positions[ms.id] = getMilestoneJunctionPosition(hostPos, hostDims, 'output', index);
    });
  });
}
